'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { setStoredStoreId } from '@/lib/store-storage';
import { BaseProps } from '@/lib/types/common';
import { validatePostalCode, formatPostalCode } from '@/lib/utils/common';
import { MAX_POSTAL_CODE_LENGTH } from '@/lib/constants';
import { Button } from '@/components';

type Props = BaseProps;

export default function PostalSearch({ base }: Props) {
  const router = useRouter();
  const [postal, setPostal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const postalCode = postal.trim().toUpperCase().replace(/\s+/g, '');
    
    if (!postalCode) {
      setError('Please enter a postal code');
      return;
    }

    // Validate Canadian postal code format
    if (!validatePostalCode(postalCode)) {
      setError('Please enter a valid Canadian postal code (e.g., T2B 3A5)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${base}/api/locations/resolve?postal=${encodeURIComponent(postalCode)}`);
      const data = await res.json();
      
      if (data?.success && data?.resolved?.covaLocationId) {
        setStoredStoreId(data.resolved.covaLocationId);
        router.push('/search');
      } else {
        setError(data?.message || 'No store found for that postal code. Please try another location.');
      }
    } catch (err) {
      setError('Unable to find store. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPostalCode(e.target.value);
    setPostal(formatted);
    setError('');
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-center">
        <div className="flex-1 max-w-md mx-auto sm:mx-0">
          <label htmlFor="postal-code" className="block text-base font-semibold text-text-primary mb-2 text-left">
            Enter Your Postal Code
          </label>
          <input
            id="postal-code"
            type="text"
            name="postal"
            value={postal}
            onChange={handleInputChange}
            placeholder="T2B 3A5"
            maxLength={MAX_POSTAL_CODE_LENGTH}
            className="w-full rounded-lg border-2 border-neutral-30  px-5 py-4 text-lg font-semibold tracking-wider text-center focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30 uppercase shadow-sm"
            disabled={loading}
          />
          {error && (
            <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
          )}
        </div>
        <div className="flex items-end justify-center sm:justify-start">
          <Button
            type="submit"
            disabled={loading || !postal.trim()}
            variant="primary"
            className="w-full sm:w-auto rounded-lg px-10 py-4"
          >
            {loading ? 'Finding...' : 'Find Store'}
          </Button>
        </div>
      </form>
      <p className="mt-4 text-base text-text-primary text-center font-medium">
        We'll find the nearest location and show you available products.
      </p>
    </div>
  );
}


